import { captureException } from '@sentry/nextjs';
import BlogHeader from 'components/BlogHeader';
import Markdown from 'components/markdown';
import { blogStyling } from 'components/markdown/styling/blog';
import {
  ENGLISH_LOCALE_IDENTIFIER,
  FORMATTED_DEFAULT_LOCALE,
} from 'constants/i18n';
import useDate from 'hooks/useDate';
import { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { APIResponseData } from 'strapi/types/utils';
import { fetchMany, fetchOne, getUidWithoutLocale } from 'strapi/utils/api';
import { i18nLocaleFormatter } from 'util/i18nLocaleFormatter';

import styles from './style.module.scss';

type StrapiCompanyBlogData =
  APIResponseData<'api::company-blog.company-blog'>['attributes'];

const FeatherInDepthBlogPost = ({
  blogPost,
}: {
  blogPost: StrapiCompanyBlogData;
}) => {
  const { title, author, publishDate, meta, markdown } = blogPost;
  const date = useDate(publishDate);
  return (
    <>
      <NextSeo title={title} description={meta?.description} />
      <div className="p-body mt80">
        <BlogHeader title={title} author={author} date={date} />
        <section className="mb48 d-block px16">
          <Markdown className={styles.body} styling={blogStyling}>
            {markdown}
          </Markdown>
        </section>
      </div>
    </>
  );
};
export default FeatherInDepthBlogPost;

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    // Limiting locale to en-de as Company blog pages are currently only supported in English
    const paths = (
      await fetchMany('company-blogs', { locale: ENGLISH_LOCALE_IDENTIFIER })
    ).map(({ uid }) => ({
      params: { uid: getUidWithoutLocale(uid) },
    }));

    return { paths, fallback: false };
  } catch (error) {
    captureException(error);
    throw new Error('Failed to fetch CMS pages during static generation');
  }
};

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  preview,
}) => {
  if (!params?.uid || typeof params.uid !== 'string') return { notFound: true };

  // Limiting locale to en-de as Company blog pages are currently only supported in English
  const blogPost = await fetchOne('company-blogs', {
    uid: params.uid,
    locale: ENGLISH_LOCALE_IDENTIFIER,
    preview,
  });

  const i18nProps = await serverSideTranslations(
    i18nLocaleFormatter(locale) || FORMATTED_DEFAULT_LOCALE,
    ['common']
  );
  return {
    props: {
      blogPost,
      ...i18nProps,
    },
  };
};
